import React, { useEffect, useState } from "react";
import "./ViewUserForm.css";

const ViewStocktakeForm = ({ selectedSession, onClose }) => {
  const { name, location_name, scope, status, started_at, ended_at, user_name } =
    selectedSession;
  const [stocktakeList, setStocktakeList] = useState([]);
  // Extra metadata returned by the /lines endpoint (department, totals, etc.)
  const [sessionMeta, setSessionMeta] = useState({});
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    if (selectedSession?.session_id) {
      fetch(`/api/stocktake/sessions/${selectedSession.session_id}/lines`)
        .then((response) => response.json())
        .then((data) => {
          if (data.rows) {
            setStocktakeList(data.rows);
            setSessionMeta(data.session || {});
            setTotalItems(
              data.total_items != null ? data.total_items : data.rows.length
            );
          } else {
            console.warn("Unexpected stocktake lines response:", data);
          }
        })
        .catch((error) => console.error("Error fetching stocktake list:", error));
    }
  }, [selectedSession]);

  // Derive scan status from expected vs counted quantities:
  //   expected >= 1 & counted >= 1  -> Found    (belongs here and detected,
  //                                              even if over-counted)
  //   expected >= 1 & counted == 0  -> Missing  (expected but not detected)
  //   expected == 0 & counted >= 1  -> Unknown  (not expected here, but detected)
  const statusOf = (it) => {
    const expected = Number(it.expected_qty) || 0;
    const counted = Number(it.counted_qty) || 0;
    if (expected >= 1 && counted >= 1) return "Found";
    if (expected >= 1 && counted === 0) return "Missing";
    if (expected === 0 && counted >= 1) return "Unknown";
    return "Found"; // expected 0 & counted 0 (nothing to flag)
  };

  const handlePrintReport = () => {
    const fmt = (d) => (d ? new Date(d).toLocaleString() : "-");
    const esc = (v) =>
      String(v ?? "-").replace(/[&<>"']/g, (c) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
      );

    // Prefer metadata from the /lines response, fall back to selectedSession
    const meta = sessionMeta || {};
    const reportName = meta.name || name;
    const reportLocation = meta.location_name || location_name;
    const reportDepartment = meta.department_name;
    const reportUser = meta.user_name || user_name;
    const reportStart = meta.started_at || started_at;
    const reportEnd = meta.ended_at || ended_at;
    const reportTotal = totalItems || stocktakeList.length;

    // Status tallies for the header summary
    let foundCount = 0;
    let missingCount = 0; // unaccounted (expected but not scanned)
    let unknownCount = 0; // scanned but not expected
    stocktakeList.forEach((it) => {
      const s = statusOf(it);
      if (s === "Found") foundCount += 1;
      else if (s === "Missing") missingCount += 1;
      else unknownCount += 1;
    });

    const rows = stocktakeList.length
      ? stocktakeList
          .map((it) => {
            const variance =
              (Number(it.counted_qty) || 0) - (Number(it.expected_qty) || 0);
            const description = [it.item_first_name, it.item_second_name]
              .filter((p) => p && p !== "")
              .join(" - ");
            return `
              <tr>
                <td>${esc(it.item_code)}</td>
                <td>${esc(description || it.description)}</td>
                <td style="text-align:center;">${esc(it.expected_qty)}</td>
                <td style="text-align:center;">${esc(it.counted_qty)}</td>
                <td style="text-align:center;">${variance}</td>
                <td>${esc(it.location_name)}</td>
                <td>${esc(statusOf(it))}</td>
              </tr>`;
          })
          .join("")
      : `<tr><td colspan="7" style="text-align:center;">No items found for this session.</td></tr>`;

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Stocktake Report - ${esc(reportName)}</title>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; color: #333; margin: 40px; }
            h1 { font-size: 22px; margin-bottom: 4px; }
            .meta { margin: 16px 0 16px; }
            .meta p { margin: 4px 0; font-size: 14px; }
            .counts { margin: 8px 0 20px; font-size: 14px; }
            .counts span { display: inline-block; margin-right: 18px; }
            h2 { font-size: 16px; margin-top: 16px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
            th, td { padding: 7px 8px; border-bottom: 1px solid #ddd; text-align: left; }
            th { background: #f8f8f8; }
            .ack { margin-top: 40px; page-break-inside: avoid; }
            .ack-table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            .ack-table td { border: none; padding: 18px 14px 8px; vertical-align: bottom; }
            .ack-role { width: 110px; font-weight: bold; font-size: 13px; white-space: nowrap; }
            .ack-field { width: 25%; }
            .ack-line { border-bottom: 1px solid #333; height: 28px; }
            .ack-label { font-size: 11px; color: #666; margin-top: 4px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <h1>Stocktake Discrepancy Report</h1>
          <div class="meta">
            <p><strong>Stocktake Name:</strong> ${esc(reportName)}</p>
            <p><strong>User:</strong> ${esc(reportUser)}</p>
            <p><strong>Department:</strong> ${esc(reportDepartment)}</p>
            <p><strong>Location:</strong> ${esc(reportLocation)}</p>
            <p><strong>Total Items in Stocktake:</strong> ${esc(reportTotal)}</p>
            <p><strong>Start:</strong> ${esc(fmt(reportStart))}</p>
            <p><strong>End:</strong> ${esc(fmt(reportEnd))}</p>
          </div>
          <div class="counts">
            <span><strong>Counted (Found):</strong> ${foundCount}</span>
            <span><strong>Unaccounted (Missing):</strong> ${missingCount}</span>
            <span><strong>Unknown:</strong> ${unknownCount}</span>
          </div>
          <h2>Item Details</h2>
          <table>
            <thead>
              <tr>
                <th>Item Code</th>
                <th>Description</th>
                <th style="text-align:center;">Asset Qty</th>
                <th style="text-align:center;">RFID Detected</th>
                <th style="text-align:center;">Variance</th>
                <th>Location</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <div class="ack">
            <h2>Acknowledgement</h2>
            <table class="ack-table">
              <tbody>
                ${["Prepared by", "Checked by", "Verified by", "Approved by"]
                  .map(
                    (role) => `
                  <tr>
                    <td class="ack-role">${role}</td>
                    <td class="ack-field">
                      <div class="ack-line"></div>
                      <div class="ack-label">Signature</div>
                    </td>
                    <td class="ack-field">
                      <div class="ack-line"></div>
                      <div class="ack-label">Name</div>
                    </td>
                    <td class="ack-field">
                      <div class="ack-line"></div>
                      <div class="ack-label">Designation</div>
                    </td>
                  </tr>`
                  )
                  .join("")}
              </tbody>
            </table>
          </div>
        </body>
      </html>`;

    // Open a print window; user can choose "Save as PDF" to download to PC
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow pop-ups to download the report.");
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  if (!selectedSession) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="close-button" onClick={onClose}>
          &times;
        </button>

        <div className="modal-header-actions">
          <button className="print-report-button" onClick={handlePrintReport}>
            Print Report
          </button>
        </div>

        <h3>Stock-Take Details</h3>
        <p><strong>Name:</strong> {name}</p>
        <p><strong>Location:</strong> {location_name || "-"}</p>
        <p><strong>Scope:</strong> {scope}</p>
        <p><strong>Status:</strong> {status}</p>
        <p><strong>Started:</strong> {started_at ? new Date(started_at).toLocaleString() : "-"}</p>
        <p><strong>Ended:</strong> {ended_at ? new Date(ended_at).toLocaleString() : "-"}</p>

        <h4>Count Lines</h4>
        <div className="UserList-container">
          {stocktakeList.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Item Code</th>
                  <th>Expected Qty</th>
                  <th>Counted Qty</th>
                  <th>Variance</th>
                </tr>
              </thead>
              <tbody>
                {stocktakeList.map((it) => (
                  <tr key={it.item_code}>
                    <td>{it.item_code}</td>
                    <td>{it.expected_qty}</td>
                    <td>{it.counted_qty}</td>
                    <td>{(it.counted_qty ?? 0) - (it.expected_qty ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No lines found for this session.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewStocktakeForm;